import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Link,
  List,
  ListItem,
  Skeleton,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import * as apiClient from "../api";
import { Question } from "../types";
import { QuestionCard } from "../components";
import EditCard from "../components/EditCard";
import CalculatorJsonDialog from "../components/CalculatorJsonDialog";

const LoadingCard = styled(Skeleton)({
  height: "6rem",
});

interface ChildCalculator {
  calculatorId: string;
  classification: string;
}

const Calculators = () => {
  const { calculatorId } = useParams();
  const [loaded, setLoaded] = useState(false);
  const [classification, setClassification] = useState("");
  const [ichdUrl, setIchdUrl] = useState("");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [responses, setResponses] = useState({});

  const [parentCalculatorId, setParentCalculatorId] = useState<null | string>(null);
  const [parentCalculatorTitle, setParentCalculatorTitle] = useState<null | string>(null);

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [result, setResult] = useState(false);
  const [ChildCalculators, setChildCalculators] = useState<ChildCalculator[]>([]);

  const title = classification ? `${classification} calculator` : "Calculator";

  const questionIds = questions.map(({ questionId }) => questionId);
  const responsesQuestionIds = Object.keys(responses);
  const allResponsesEntered =
    questionIds.length === responsesQuestionIds.length &&
    questionIds.every((questionId) =>
      responsesQuestionIds.includes(questionId),
    );

  const loadQuestions = async () => {
    const res = await apiClient.getCalculator(calculatorId as string);
    setClassification(res.data.classification);
    setIchdUrl(res.data.ichdUrl);
    setQuestions(res.data.questions);
    setLoaded(true);

    if (res.data.parentCalculator) {
      setParentCalculatorId(res.data.parentCalculator.calculatorId);
      setParentCalculatorTitle(res.data.parentCalculator.classification);
    }
  };

  useEffect(() => {
    loadQuestions();
  }, [calculatorId]);

  const updateResponses = (
    questionId: string,
    value: string | number | boolean,
  ) => {
    setResponses({ ...responses, [questionId]: value });
  };

  const handleSubmit = async () => {
    const res = await apiClient.useCalculator(
      calculatorId as string,
      responses,
    );
    setResult(res.data.result);
    setChildCalculators(res.data.childCalculators);
    setHasSubmitted(true);
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h4">{title}</Typography>
        <EditCalculator calculatorId={calculatorId!} onChange={() => { setLoaded(false); loadQuestions(); }} />
      </Stack>
      {!loaded ? (

        <Stack spacing={2}>
          <LoadingCard variant="rounded" />
          <LoadingCard variant="rounded" />
          <LoadingCard variant="rounded" />
        </Stack>
      ) : (
        <>
          {parentCalculatorId && (
            <Alert severity="info">
              <AlertTitle>Subset of <Link href={`/calculators/${parentCalculatorId}`}>{parentCalculatorTitle}</Link></AlertTitle>
              This classification assumes that is based on <Link href={`/calculators/${parentCalculatorId}`}>{parentCalculatorTitle}</Link>.
            </Alert>
          )}

          <Stack spacing={2}>
            {questions.map((question, idx) => (
              <QuestionCard
                key={question.questionId}
                index={idx + 1}
                question={question}
                updateResponses={updateResponses}
              />
            ))}
          </Stack>
        </>
      )}
      <Button disabled={!loaded || !allResponsesEntered} onClick={handleSubmit}>
        Get my result
      </Button>
      {hasSubmitted && result ? (
        <Alert severity="error">
          <AlertTitle>You may have {classification}</AlertTitle>
          <Stack spacing={1}>
            <Box>
              Please check if the headache is not better fulfilling another
              ICHD-3 diagnosis.
            </Box>
            <Box>
              {classification} criteria can be found at{" "}
              <Link href={ichdUrl} target="_blank" rel="noopener">
                {ichdUrl}
              </Link>
              .
            </Box>
            {
              ChildCalculators.length > 0 && (
                <Box>
                  More specific classifications could be:
                  <List sx={{ listStyleType: 'disc' }}>
                    {ChildCalculators.map(({ calculatorId, classification }) => (
                      <ListItem sx={{ display: 'list-item' }}>
                        <Link key={calculatorId} href={`/calculators/${calculatorId}`}>
                          {classification}
                        </Link>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )
            }
          </Stack>
        </Alert>
      ) : (
        hasSubmitted && (
          <Alert>
            <AlertTitle>
              You do not fulfill the criteria for {classification}
            </AlertTitle>
            {classification} criteria can be found at{" "}
            <Link href={ichdUrl} target="_blank" rel="noopener">
              {ichdUrl}
            </Link>
            .
          </Alert>
        )
      )
      }
    </Stack >
  );
};

export default Calculators;

const EditCalculator = ({ calculatorId, onChange }: { calculatorId: string, onChange: () => void }) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <EditCard onClick={handleOpen} />
      <CalculatorJsonDialog handleClose={handleClose} open={open} variant="edit" calculatorId={calculatorId} onChange={onChange} />
    </>
  );
}
